import * as React from 'react'
import { useState } from 'react'
import { Button, Typography, Tooltip } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashCan } from '@fortawesome/pro-duotone-svg-icons/faTrashCan'
import { faTrashAlt } from '@fortawesome/pro-duotone-svg-icons/faTrashAlt'
import LoadingDefault from '@/Components/Elements/Loading/LoadingDefault'
import AppDialog from '@/Components/AppDialog/AppDialog'
import { StyledButton } from '@/Components/Elements/StyledComponents/Buttons/StyledButton'
import { DeletedButtonProps } from './types'

const DeletedMessage: React.FC<DeletedButtonProps> = ({
    kedo,
    messageId,
    getCheckedItems,
    updateMessage,
}) => {
    const [showModal, setShowModal] = useState<boolean>(false)
    const [isDeleting, setIsDeleting] = useState<boolean>(false)
    const url = kedo.api().getMessageEndpoint()

    const deleteMessageId = async () => {
        setIsDeleting(true)
        try {
            await kedo.api().delete(`${url}/${messageId}`)
            setIsDeleting(false)
            setShowModal(false)
            updateMessage()
        } catch (error) {
            setIsDeleting(false)
            throw error
        }
    }

    const deleteMessage = async () => {
        if (messageId) return deleteMessageId()

        const checkedItems = getCheckedItems()
        let idsToDelete = checkedItems.map((item) => item.id)
        if (!Array.isArray(idsToDelete)) idsToDelete = [idsToDelete]

        setIsDeleting(true)
        try {
            await Promise.all(
                idsToDelete.map((id) => kedo.api().delete(`${url}/${id}`))
            )
            setIsDeleting(false)
            setShowModal(false)
            updateMessage()
        } catch (error) {
            setIsDeleting(false)
        }
    }

    const warningMessage = () => setShowModal(true)

    return (
        <>
            <Tooltip title={kedo.t('Delete')} arrow>
                <StyledButton
                    color={messageId ? 'primary' : 'secondary'}
                    onClick={warningMessage}
                    startIcon={<FontAwesomeIcon icon={faTrashCan} />}
                    sx={{
                        height: 38,
                        minWidth: 'unset',
                        '& .MuiButton-startIcon': { ml: 0 },
                        ...(messageId && {
                            width: 40,
                            '& .MuiButton-startIcon': { margin: 0 },
                        }),
                        '& .MuiButton-startIcon svg': { fontSize: '16px' },
                    }}
                >
                    {!messageId && kedo.t('Delete')}
                </StyledButton>
            </Tooltip>

            <AppDialog
                open={showModal}
                onClose={() => setShowModal(false)}
                maxWidth="xs"
                fullWidth
                title={kedo.t('Confirm delete')}
                actions={
                    <>
                        <Button
                            onClick={deleteMessage}
                            variant="contained"
                            color="error"
                            startIcon={
                                isDeleting ? (
                                    <LoadingDefault size="sm" as="span" />
                                ) : (
                                    <FontAwesomeIcon icon={faTrashAlt} />
                                )
                            }
                            sx={{ textTransform: 'none' }}
                        >
                            {kedo.t('Delete')}
                        </Button>
                        <StyledButton
                            onClick={() => setShowModal(false)}
                            color="secondary"
                            sx={{ height: 38 }}
                        >
                            {kedo.t('Cancel')}
                        </StyledButton>
                    </>
                }
            >
                <Typography>
                    {kedo.t('Are you sure you want to delete these messages?')}
                </Typography>
            </AppDialog>
        </>
    )
}

export default DeletedMessage
